import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { categories, siteInfo, valueProps } from "@/mocks/site";

const principles = [
  {
    icon: "ri-quill-pen-line",
    title: "原创表达",
    desc: "所有介绍与解析由编辑部撰写，用自己的话把作品讲清楚，而不是复制简介。",
  },
  {
    icon: "ri-shield-check-line",
    title: "尊重创作",
    desc: "我们只做介绍、推荐与讨论，不提供也未授权任何影视资源，鼓励在正版渠道观看。",
  },
  {
    icon: "ri-scales-3-line",
    title: "克制判断",
    desc: "不使用夸大的评分与数据，把“适不适合你”的判断权交回读者。",
  },
  {
    icon: "ri-links-line",
    title: "信息可查",
    desc: "作品资料以公开的创作信息为基础整理，力求准确，发现偏差会及时更正。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 樱花动漫社的定位与内容原则",
    description:
      "了解樱花动漫社：一个以编辑视角运营的动漫资讯与推荐平台，专注作品介绍、新番速递、制作幕后与观看指南，尊重创作、不提供在线播放。",
    keywords: "关于樱花动漫社,动漫资讯平台,内容原则,编辑团队",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们",
        url: `${siteInfo.domain}/about`,
        inLanguage: "zh-CN",
        description: "樱花动漫社的网站定位、内容方向与内容原则。",
        about: { "@type": "Organization", name: siteInfo.name, url: siteInfo.domain },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${siteInfo.domain}/about` },
        ],
      },
    ],
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      {/* 引言 */}
      <section className="mt-6 overflow-hidden rounded-2xl bg-primary-950 px-6 py-12 text-primary-50 md:px-12 md:py-16">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-900/60 px-4 py-1.5 text-xs">
          <i className="ri-flower-fill" aria-hidden="true"></i>
          关于樱花动漫社
        </span>
        <h1 className="mt-5 max-w-3xl font-heading text-3xl font-700 leading-snug md:text-4xl">
          我们想做的，是帮你把一部动画真正看清楚
        </h1>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-primary-100/85 md:text-base">
          {siteInfo.intro}
        </p>
      </section>

      {/* 定位 */}
      <section className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <h2 className="font-heading text-2xl font-700 text-foreground-950">网站定位</h2>
          <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700 md:text-base">
            <p>
              樱花动漫社是一个以编辑视角运营的动漫内容站。我们不追求收录最多，而是希望每一篇内容都
              经过认真整理：这部作品讲了一个什么故事、它在哪些技术细节上下了功夫、适合什么心情去看。
            </p>
            <p>
              我们服务的是这样一群人：他们会为了一段配乐反复重看，会因为一个镜头的处理去查制作名单，
              也愿意花时间把一部作品慢慢看完。
            </p>
          </div>
        </div>
        <div className="rounded-2xl bg-background-100 p-6 md:p-8">
          <h3 className="font-heading text-lg font-600 text-foreground-950">我们希望提供的价值</h3>
          <ul className="mt-5 space-y-4">
            {[
              "减少选择成本：用情绪方向代替冷冰冰的排名。",
              "提供理解工具：让你看懂叙事节奏、色彩与配乐的作用。",
              "保持信息诚实：不夸大、不虚构，只讲能讲清楚的事。",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true"></i>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 内容方向 */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl font-700 text-foreground-950">内容方向</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          目前内容主要分为两条线：一条是持续整理的作品资料页，一条是定期更新的资讯专栏。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/works?category=${cat.id}`}
              className="group rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${cat.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-600 text-foreground-950 group-hover:text-primary-700">
                {cat.name}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{cat.desc}</p>
            </Link>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {valueProps.map((item) => (
            <div key={item.title} className="rounded-xl border border-background-200 bg-secondary-50 p-5">
              <i className={`${item.icon} text-xl text-secondary-700`} aria-hidden="true"></i>
              <h3 className="mt-3 font-heading text-base font-600 text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 内容原则 */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl font-700 text-foreground-950">内容原则</h2>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {principles.map((item) => (
            <div key={item.title} className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-600 text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 联系 */}
      <section className="mt-16 rounded-2xl bg-secondary-50 p-8 md:p-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <h2 className="font-heading text-2xl font-700 text-foreground-950">内容更新与联系</h2>
            <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700 md:text-base">
              <p>
                作品资料会随着整理进度持续补充，资讯专栏则跟随每季新番节奏更新，并不定期推出
                制作幕后与作品解析专题。
              </p>
              <p>
                如果你发现资料需要更正，或想推荐一部作品，欢迎通过邮箱联系我们，我们会在核对后处理。
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-center rounded-xl border border-secondary-200 bg-background-50 p-6">
            <p className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-mail-line text-primary-600" aria-hidden="true"></i>
              编辑部邮箱
            </p>
            <a
              href={`mailto:${siteInfo.email}`}
              className="mt-2 font-heading text-lg font-600 text-primary-700 hover:text-primary-800"
            >
              {siteInfo.email}
            </a>
            <Link
              to="/faq"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-500 text-foreground-800 transition-colors hover:bg-background-100"
            >
              <i className="ri-question-answer-line" aria-hidden="true"></i>
              查看常见问题
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}